/** Shopify integration panel with App Store and internal development-store flows. */
import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Loader2,
  ShoppingBag,
  TrendingUp,
  Package,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Clock,
  CheckCircle2,
  type LucideIcon,
} from "lucide-react";

type ShopifyStatus = {
  connected: boolean;
  shopDomain?: string;
  shopName?: string;
  appStoreListingUrl?: string | null;
  canSelfServeInstall: boolean;
  /** Merchant odinstalował aplikację (app/uninstalled) — trzeba zainstalować ponownie. */
  uninstalledAt?: string | null;
  /** Martwy token (refresh odrzucony / Admin API 401) — reinstalacja odnawia parę. */
  needsReauth?: boolean;
  /** Scope'y z konfiguracji, których ta instalacja nie dostała (np. brak write_pixels). */
  missingScopes?: string[];
  /**
   * Web Pixel Extension instaluje się sam po podłączeniu. `active: true` = Shopify
   * potwierdził piksel (Custom Pixel zbędny); `false`/`null` = pokaż fallback.
   */
  webPixel?: { active: boolean | null };
};

type ShopifyConnectPanelProps = {
  onInsightsLoaded?: (context: string) => void;
  forUserId?: string;
  /**
   * Hub integracji: podsumowanie sklepu ładuje się samo (cache 5 min), przycisk
   * działa jako „Odśwież". Czat zostaje przy ręcznym „Pobierz dane", bo tam
   * kliknięcie = świadome dołączenie danych do kontekstu rozmowy.
   */
  autoLoadInsights?: boolean;
};

const INSIGHTS_STALE_TIME_MS = 5 * 60_000;

type ShopifyTopProduct = {
  title?: string | null;
  quantity?: number | string | null;
  revenue?: number | string | null;
};

type ShopifyInsightsResponse = {
  context: string;
  summary?: {
    totalProducts: number;
    activeProducts: number;
    totalOrders: number;
    totalRevenue: number | string;
    avgOrderValue: number | string;
    currency?: string | null;
    topProducts?: ShopifyTopProduct[];
  };
};

type StatTone = "green" | "blue" | "emerald" | "amber";

// Lookup zamiast interpolacji klas — Tailwind nie widzi klas składanych w runtime.
const STAT_TONE_CLASSES: Record<StatTone, { box: string; accent: string }> = {
  green: { box: "bg-green-500/10 border-green-500/20", accent: "text-green-400" },
  blue: { box: "bg-blue-500/10 border-blue-500/20", accent: "text-blue-400" },
  emerald: { box: "bg-emerald-500/10 border-emerald-500/20", accent: "text-emerald-400" },
  amber: { box: "bg-amber-500/10 border-amber-500/20", accent: "text-amber-400" },
};

type StatTileProps = {
  tone: StatTone;
  icon: LucideIcon;
  label: string;
  value: string | number;
  sub?: string;
};

/**
 * Kafelek KPI o stałej siatce: nagłówek ma zarezerwowane 2 linie (długie
 * etykiety typu „Śr. zamówienie" łamią się bez spychania wartości), a linia
 * pomocnicza jest zawsze renderowana — dzięki temu wartości we wszystkich
 * czterech kafelkach leżą na tej samej wysokości.
 */
function StatTile({ tone, icon: Icon, label, value, sub }: StatTileProps) {
  const classes = STAT_TONE_CLASSES[tone];
  return (
    <div className={`flex flex-col border rounded-xl p-3 ${classes.box}`}>
      <div className="flex items-start gap-2 min-h-[1.75rem]">
        <Icon className={`h-3.5 w-3.5 shrink-0 mt-px ${classes.accent}`} />
        <span className="text-[11px] leading-[0.875rem] text-white/60 uppercase">{label}</span>
      </div>
      <div className={`text-xl font-bold leading-tight mt-1 tabular-nums ${classes.accent}`}>
        {value}
      </div>
      <span className="text-[10px] leading-[0.875rem] min-h-[0.875rem] text-white/40 mt-auto">
        {sub ?? ""}
      </span>
    </div>
  );
}

function formatMoney(amount: number | string, currency?: string | null): string {
  return `${(Number(amount) || 0).toFixed(0)} ${currency ?? ""}`.trim();
}

const ShopifyLogo = () => (
  <div className="w-6 h-6 rounded bg-shopify flex items-center justify-center">
    <ShoppingBag className="w-3.5 h-3.5 text-white" />
  </div>
);

export default function ShopifyConnectPanel({
  onInsightsLoaded,
  forUserId,
  autoLoadInsights = false,
}: ShopifyConnectPanelProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [insightsData, setInsightsData] = useState<ShopifyInsightsResponse | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [shopDomain, setShopDomain] = useState("");
  const fuParam = forUserId ? `?${new URLSearchParams({ forUserId }).toString()}` : "";

  const {
    data: status,
    isLoading,
    isError,
    refetch,
  } = useQuery<ShopifyStatus>({
    queryKey: ["shopify-integration-status", forUserId],
    queryFn: () => apiRequest("GET", `/api/integrations/shopify/status${fuParam}`),
    staleTime: 30_000,
    // 403 (brak dostępu) / 500 nie może wyglądać jak „Nie połączono" z przyciskiem
    // instalacji — pokazujemy stan błędu, bez trzykrotnego retry na 403.
    retry: false,
  });

  const disconnectMutation = useMutation({
    mutationFn: () =>
      apiRequest(
        "POST",
        "/api/integrations/shopify/disconnect",
        forUserId ? { forUserId } : undefined
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["shopify-integration-status"] });
      // Nagłówek karty w hubie ma własny klucz (staleTime 60 s) — bez tego przez
      // minutę pokazywał „Połączono" po odłączeniu.
      queryClient.invalidateQueries({ queryKey: ["hub-shopify"] });
      queryClient.removeQueries({ queryKey: ["shopify-insights"] });
      setInsightsData(null);
    },
  });

  const fetchInsights = async (): Promise<ShopifyInsightsResponse> =>
    (await apiRequest(
      "POST",
      "/api/integrations/shopify/insights",
      forUserId ? { forUserId } : undefined
    )) as ShopifyInsightsResponse;

  // Ręczna ścieżka (czat): kliknięcie dołącza kontekst do rozmowy.
  const insightsMutation = useMutation({
    mutationFn: fetchInsights,
    onSuccess: (data) => {
      setInsightsData(data);
      onInsightsLoaded?.(data.context);
    },
  });

  // Automatyczna ścieżka (hub): /insights to żywe Admin API (wszystkie produkty
  // + zamówienia), więc cache 5 min i zero retry — martwy token i tak wraca 502.
  const canLoadInsights = status?.connected === true && status.needsReauth !== true;
  const insightsQuery = useQuery<ShopifyInsightsResponse>({
    queryKey: ["shopify-insights", forUserId],
    queryFn: fetchInsights,
    enabled: autoLoadInsights && canLoadInsights,
    staleTime: INSIGHTS_STALE_TIME_MS,
    retry: false,
  });

  const insights = autoLoadInsights ? (insightsQuery.data ?? null) : insightsData;
  const insightsLoading = autoLoadInsights ? insightsQuery.isFetching : insightsMutation.isPending;
  const loadInsights = () =>
    autoLoadInsights ? insightsQuery.refetch() : insightsMutation.mutate();

  const installMutation = useMutation({
    mutationFn: async () => {
      const params = new URLSearchParams({ shop: shopDomain });
      if (forUserId) params.set("forUserId", forUserId);
      return (await apiRequest(
        "GET",
        `/api/integrations/shopify/oauth/install?${params.toString()}`
      )) as { url: string };
    },
    onSuccess: ({ url }) => window.location.assign(url),
    onError: (error) => {
      toast({
        title: t("connectPanels.shopify.brand"),
        description:
          error instanceof Error ? error.message : t("connectPanels.shopify.installError"),
        variant: "destructive",
      });
    },
  });

  const startDevelopmentStoreInstall = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!shopDomain.trim()) return;
    installMutation.mutate();
  };

  const listingUrl = status?.appStoreListingUrl ?? null;
  const summary = insights?.summary;
  const topProducts = summary?.topProducts ?? [];

  const [pixelSnippetLoading, setPixelSnippetLoading] = useState(false);
  async function copyPixelSnippet() {
    setPixelSnippetLoading(true);
    try {
      const res = (await apiRequest("GET", "/api/integrations/shopify/custom-pixel-snippet")) as {
        snippet: string;
      };
      await navigator.clipboard.writeText(res.snippet);
      toast({
        title: t("connectPanels.shopify.brand"),
        description: t("connectPanels.shopify.pixelCopied"),
      });
    } catch (err) {
      toast({
        title: t("connectPanels.shopify.brand"),
        description:
          err instanceof Error ? err.message : t("connectPanels.shopify.pixelFetchError"),
        variant: "destructive",
      });
    } finally {
      setPixelSnippetLoading(false);
    }
  }

  if (isLoading) {
    return (
      <div className="p-3 bg-white/[0.04] border border-white/[0.08] rounded-lg flex items-center gap-2">
        <Loader2 className="h-4 w-4 animate-spin text-green-400" />
        <span className="text-sm text-white/60">{t("connectPanels.shopify.checking")}</span>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="p-3 bg-white/[0.04] border border-red-500/30 rounded-lg space-y-2">
        <div className="flex items-center gap-2">
          <ShopifyLogo />
          <span className="text-sm font-medium text-white">{t("connectPanels.shopify.brand")}</span>
          <Badge variant="outline" className="text-xs border-red-500/40 text-red-300">
            {t("connectPanels.shopify.statusError")}
          </Badge>
        </div>
        <Button size="sm" variant="outline" className="text-xs" onClick={() => refetch()}>
          {t("connectPanels.shopify.retry")}
        </Button>
      </div>
    );
  }

  if (!status?.connected) {
    return (
      <div className="p-3 bg-white/[0.04] border border-white/[0.08] rounded-lg space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ShopifyLogo />
            <span className="text-sm font-medium text-white">
              {t("connectPanels.shopify.brand")}
            </span>
            <Badge variant="outline" className="text-xs border-white/[0.1] text-white/60">
              {t("connectPanels.common.notConnected")}
            </Badge>
          </div>
        </div>
        {status?.uninstalledAt && status.shopDomain && (
          <p className="text-xs text-amber-300/90">
            {t("connectPanels.shopify.uninstalledDescription", { shopDomain: status.shopDomain })}
          </p>
        )}
        {listingUrl ? (
          <>
            <p className="text-xs text-white/50">
              {t("connectPanels.shopify.appStoreDescription")}
            </p>
            <Button
              asChild
              size="sm"
              className="bg-shopify hover:bg-shopify-dark text-white text-xs"
            >
              <a href={listingUrl} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="h-3 w-3 mr-1" />
                {t("connectPanels.shopify.installFromAppStore")}
              </a>
            </Button>
          </>
        ) : (
          <>
            <p className="text-xs text-white/50">
              {t("connectPanels.shopify.approvalPendingDescription")}
            </p>
            <Button size="sm" disabled className="text-xs">
              <Clock className="h-3 w-3 mr-1" />
              {t("connectPanels.shopify.approvalPendingButton")}
            </Button>
            <p className="text-[11px] text-white/40">{t("connectPanels.shopify.pilotHint")}</p>
          </>
        )}
        {/* VP-317: karta Shopify jedzie kontekstem konta zalogowanego (claim sklepu nie
            zna klienta agencji), więc agencja z auto-wybranym klientem musi wiedzieć, na
            czyje konto podepnie sklep — inaczej podpina „w ciemno" i nadpisuje własną
            integrację. Wybór klienta docelowego to osobne zadanie. */}
        <p className="text-[11px] text-white/40">{t("connectPanels.shopify.ownAccountScope")}</p>
        {status?.canSelfServeInstall && (
          <form
            onSubmit={startDevelopmentStoreInstall}
            className="space-y-2 border-t border-white/[0.08] pt-3"
          >
            <div>
              <p className="text-xs font-medium text-white/80">
                {t("connectPanels.shopify.developmentStoreTitle")}
              </p>
              <p className="text-[11px] text-white/40">
                {t("connectPanels.shopify.developmentStoreHint")}
              </p>
            </div>
            <div className="flex gap-2">
              <Input
                value={shopDomain}
                onChange={(event) => setShopDomain(event.target.value)}
                placeholder={t("connectPanels.shopify.shopDomainPlaceholder")}
                aria-label={t("connectPanels.shopify.shopDomainLabel")}
                className="h-8 text-xs"
                autoComplete="off"
              />
              <Button
                type="submit"
                size="sm"
                disabled={!shopDomain.trim() || installMutation.isPending}
                className="text-xs"
              >
                {installMutation.isPending ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  t("connectPanels.shopify.startInstall")
                )}
              </Button>
            </div>
          </form>
        )}
      </div>
    );
  }

  const needsReauth = status.needsReauth === true;
  const missingScopes = status.missingScopes ?? [];
  const webPixelActive = status.webPixel?.active === true;

  return (
    <div
      className={`p-3 bg-white/[0.04] border rounded-lg space-y-3 ${
        needsReauth ? "border-amber-500/40" : "border-shopify/30"
      }`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ShopifyLogo />
          <span className="text-sm font-medium text-white">{t("connectPanels.shopify.brand")}</span>
          {needsReauth ? (
            <Badge className="text-xs bg-amber-500/20 text-amber-300 border-amber-500/50">
              {t("connectPanels.shopify.needsReauthTitle")} · {status.shopName || status.shopDomain}
            </Badge>
          ) : (
            <Badge className="text-xs bg-green-600/20 text-green-400 border-green-500/50">
              {t("connectPanels.common.connected")} · {status.shopName || status.shopDomain}
            </Badge>
          )}
        </div>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => disconnectMutation.mutate()}
          disabled={disconnectMutation.isPending}
          className="text-xs text-red-400 hover:text-red-300 hover:bg-red-500/10"
        >
          {t("connectPanels.common.disconnect")}
        </Button>
      </div>

      {needsReauth && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-2.5 space-y-2">
          <p className="text-xs text-amber-200/90">
            {t("connectPanels.shopify.needsReauthDescription")}
          </p>
          {listingUrl ? (
            <Button
              asChild
              size="sm"
              className="bg-shopify hover:bg-shopify-dark text-white text-xs"
            >
              <a href={listingUrl} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="h-3 w-3 mr-1" />
                {t("connectPanels.shopify.reinstall")}
              </a>
            </Button>
          ) : (
            <p className="text-[11px] text-white/50">{t("connectPanels.shopify.reinstallHint")}</p>
          )}
        </div>
      )}
      {!needsReauth && missingScopes.length > 0 && (
        <p className="text-xs text-amber-300/90">
          {t("connectPanels.shopify.missingScopes", { scopes: missingScopes.join(", ") })}
        </p>
      )}

      <div className="flex items-center gap-2">
        <Button
          size="sm"
          onClick={() => void loadInsights()}
          disabled={insightsLoading || needsReauth}
          className="bg-shopify hover:bg-shopify-dark text-white text-xs"
        >
          {insightsLoading ? (
            <>
              <Loader2 className="h-3 w-3 animate-spin mr-1" /> {t("connectPanels.common.fetching")}
            </>
          ) : (
            t(autoLoadInsights ? "connectPanels.common.refresh" : "connectPanels.common.fetch")
          )}
        </Button>
        {!webPixelActive && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => copyPixelSnippet()}
            disabled={pixelSnippetLoading}
            className="text-xs"
          >
            {pixelSnippetLoading ? (
              <>
                <Loader2 className="h-3 w-3 animate-spin mr-1" />
                {t("connectPanels.shopify.pixelFetching")}
              </>
            ) : (
              t("connectPanels.shopify.copyPixel")
            )}
          </Button>
        )}
      </div>
      {webPixelActive ? (
        <p className="flex items-center gap-1 text-[11px] text-green-400/80">
          <CheckCircle2 className="h-3 w-3" />
          {t("connectPanels.shopify.pixelAutoInstalled")}
        </p>
      ) : (
        <p className="text-[11px] text-white/40">{t("connectPanels.shopify.pixelInstructions")}</p>
      )}

      {summary && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
            <StatTile
              tone="green"
              icon={Package}
              label={t("connectPanels.common.products")}
              value={summary.totalProducts}
              sub={t("connectPanels.shopify.activeProducts", { count: summary.activeProducts })}
            />
            <StatTile
              tone="blue"
              icon={ShoppingBag}
              label={t("connectPanels.common.orders")}
              value={summary.totalOrders}
            />
            <StatTile
              tone="emerald"
              icon={TrendingUp}
              label={t("connectPanels.common.revenue")}
              value={formatMoney(summary.totalRevenue, summary.currency)}
            />
            <StatTile
              tone="amber"
              icon={TrendingUp}
              label={t("connectPanels.shopify.avgOrder")}
              value={formatMoney(summary.avgOrderValue, summary.currency)}
            />
          </div>

          {topProducts.length > 0 && (
            <div>
              <button
                onClick={() => setShowDetails(!showDetails)}
                className="flex items-center gap-1.5 text-xs text-white/60 hover:text-white/70 transition-colors w-full"
              >
                {showDetails ? (
                  <ChevronUp className="h-3.5 w-3.5" />
                ) : (
                  <ChevronDown className="h-3.5 w-3.5" />
                )}
                <span className="font-medium">
                  {t("connectPanels.shopify.topProducts", {
                    count: Math.min(topProducts.length, 10),
                  })}
                </span>
              </button>
              {showDetails && (
                <div className="mt-2 rounded-lg border border-white/[0.08] overflow-hidden">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-card/80 text-white/60">
                        <th className="text-left p-2 font-medium">
                          {t("connectPanels.common.product")}
                        </th>
                        <th className="text-right p-2 font-medium">
                          {t("connectPanels.common.sold")}
                        </th>
                        <th className="text-right p-2 font-medium">
                          {t("connectPanels.common.revenue")}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {topProducts.slice(0, 10).map((p: ShopifyTopProduct, i: number) => {
                        const revenue = Number(p.revenue) || 0;
                        const quantity = Number(p.quantity) || 0;
                        return (
                          <tr key={i} className="border-t border-white/[0.06]">
                            <td className="p-2 text-white max-w-[200px] truncate">
                              {p.title ?? "—"}
                            </td>
                            <td className="p-2 text-right text-blue-400">{quantity}</td>
                            <td className="p-2 text-right text-emerald-400">
                              {revenue.toFixed(0)} {summary.currency ?? ""}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          <div className="flex items-center gap-2">
            <Badge className="text-xs bg-green-600/20 text-green-400 border-green-500/50">
              {t("connectPanels.common.dataLoaded")}
            </Badge>
            {/* „Dołączone do kontekstu AI" ma sens tylko tam, gdzie ktoś ten kontekst odbiera (czat). */}
            {onInsightsLoaded && (
              <span className="text-xs text-white/40">
                {t("connectPanels.shopify.contextAttached")}
              </span>
            )}
          </div>
        </>
      )}
    </div>
  );
}
